import React, { useMemo } from 'react';
import { Button, Empty, Tag, Typography } from 'antd';
import { LeftOutlined, PlayCircleFilled, RightOutlined } from '@ant-design/icons';
import moment from 'moment-timezone';

const { Text } = Typography;

function shiftLabel(shift, tz) {
  const name = shift.activity?.name || 'Shift';
  const start = moment.tz(shift.starts_at, tz).format('h:mm A');
  const end = moment.tz(shift.ends_at, tz).format('h:mm A');
  return { name, start, end };
}

/**
 * Week list of the current user's scheduled shifts, with Start on each row.
 * Activity name is treated as the Time Tracker "task".
 */
export default function MyScheduleCalendar({
  shifts,
  timezone,
  weekStart,
  onPrevWeek,
  onNextWeek,
  onThisWeek,
  onStartShift,
  startingShiftId,
  disabled,
}) {
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => weekStart.clone().add(i, 'days')),
    [weekStart],
  );
  const todayKey = moment.tz(timezone).format('YYYY-MM-DD');
  const rangeLabel = `${weekStart.format('MMM D')} – ${weekStart
    .clone()
    .add(6, 'days')
    .format('MMM D, YYYY')}`;

  const daysWithShifts = days.filter((day) => {
    const key = day.format('YYYY-MM-DD');
    return shifts.some(
      (s) => moment.tz(s.starts_at, timezone).format('YYYY-MM-DD') === key,
    );
  });

  return (
    <div className="clockify-my-schedule">
      <div className="clockify-my-schedule-toolbar">
        <Button size="small" icon={<LeftOutlined />} onClick={onPrevWeek} />
        <Button size="small" onClick={onThisWeek}>
          This week
        </Button>
        <Button size="small" icon={<RightOutlined />} onClick={onNextWeek} />
        <Text strong className="clockify-my-schedule-range">
          {rangeLabel}
        </Text>
      </div>

      {!daysWithShifts.length ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No scheduled shifts this week"
        />
      ) : (
        <div className="clockify-my-schedule-days">
          {daysWithShifts.map((day) => {
            const key = day.format('YYYY-MM-DD');
            const dayShifts = shifts.filter(
              (s) =>
                moment.tz(s.starts_at, timezone).format('YYYY-MM-DD') === key,
            );
            const isToday = key === todayKey;
            return (
              <div
                key={key}
                className={
                  'clockify-my-schedule-day' +
                  (isToday ? ' clockify-my-schedule-day--today' : '')
                }
              >
                <div className="clockify-my-schedule-day-head">
                  <Text strong>
                    {day.format('ddd, MMM D')}
                    {isToday ? ' · Today' : ''}
                  </Text>
                </div>
                {dayShifts.map((shift) => {
                  const { name, start, end } = shiftLabel(shift, timezone);
                  const color =
                    shift.color || shift.activity?.color || '#1890ff';
                  const open = Math.max(
                    0,
                    Number(shift.capacity || 1) -
                      (shift.assignees || []).length,
                  );
                  return (
                    <div key={shift._id} className="clockify-my-schedule-row">
                      <span
                        className="clockify-my-schedule-swatch"
                        style={{ background: color }}
                      />
                      <div className="clockify-my-schedule-meta">
                        <Text strong>{name}</Text>
                        <Text type="secondary">
                          {start} – {end}
                          {open > 0 ? ` · ${open} open` : ''}
                        </Text>
                      </div>
                      {open > 0 && (
                        <Tag color="error" style={{ marginRight: 8 }}>
                          Open
                        </Tag>
                      )}
                      <Button
                        type="primary"
                        size="small"
                        icon={<PlayCircleFilled />}
                        disabled={disabled}
                        loading={startingShiftId === shift._id}
                        onClick={() => onStartShift(shift)}
                      >
                        Start
                      </Button>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
